import { isRouteErrorResponse, Link, Outlet, useRouteError, type HeadersFunction, type MetaFunction } from 'react-router'
import NotFound from '@/site/components/NotFound'
import { errorMeta } from '@/site/lib/seo'

/**
 * Pathless layout around the site pages: its error boundary keeps the header and footer on 404s and
 * failures. The status code itself comes from the response (404 for unknown pages, 503 when the API is unavailable,
 * 500 for anything else).
 */
export default function SitePages() {
  return <Outlet />
}

/** A failed page's headers (a 503's Retry-After) reach the response. */
export const headers: HeadersFunction = ({ errorHeaders }) => errorHeaders ?? new Headers()

/** Pages set their own metadata; this only applies while the boundary shows an error. */
export const meta: MetaFunction = ({ error }) =>
  error ? errorMeta(isRouteErrorResponse(error) ? error.status : undefined) : []

export function ErrorBoundary() {
  const error = useRouteError()
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFound />

  // A 503 (the API was unavailable) is already logged by the HTTP client.
  if (!isRouteErrorResponse(error) || error.status !== 503) console.error(error)
  return <ErrorMessage />
}

/** "Something went wrong" page content. */
export function ErrorMessage() {
  return (
    <div className="container mx-auto flex flex-col justify-center items-center my-32 gap-4">
      <img loading="lazy" src="/img/quilton.svg" alt="" className="w-32 h-32" />
      <div className="font-semibold text-xl">Something went wrong loading this page.</div>
      <Link to="/" reloadDocument className="bg-bittersweet text-white text-center rounded-lg text-lg font-bold px-4 py-2">
        Back to Quilting Tutorials
      </Link>
    </div>
  )
}
